/**
 * `useCreateDeal` — single entry point for opening the canonical Create Deal
 * slideout pre-filled and pre-associated from a contact / company / selection.
 *
 * The same form is opened from several surfaces:
 *   - Contacts list bulk-action bar (selected contacts → one deal).
 *   - Contacts list row action and contact record kebab menu (single contact).
 *   - Companies list row action and company record kebab menu (single company).
 *
 * Centralizing the create config here means the deal's
 * `createProperties` / `createAssociations` / `entityType` and the
 * `dealService.create` submit path are sourced in ONE place, and the smart
 * auto-naming rules live alongside them rather than being re-derived per caller.
 *
 * Smart naming rules:
 *   - Single contact            → "New deal with {contact}"
 *   - Single company            → "New deal with {company}"
 *   - Many contacts, one shared company → "New deal with {company}"
 *   - Many contacts, 0 / >1 companies   → "New deal"
 *
 * For the contacts bulk action, "select all" mode is capped at
 * `MAX_BULK_DEAL_CONTACTS` contacts on a single deal (associating thousands of
 * contacts with one deal is not a real workflow); the user is toasted when the
 * selection is capped.
 */

import { useCallback } from 'react';
import { toast } from 'sonner';

import { useUser } from '@/allauth_auth/hooks';
import { createCRMObjectClient } from '@/lib/crm/core/CRMObjectClient';
import { COMPANY, CONTACT, DEAL } from '@/lib/crm/core/entityTypes';
import { buildGenericCreateConfig, useCreateSlideout } from '@/lib/crm/create';
import { getEntityTypeConfig } from '@/lib/crm/record/registry/entityTypeRegistry';
import { dealService } from '@/lib/crm/services';
import { getDisplayName } from '@/lib/crm/utils/getDisplayName';
import { logger } from '@/lib/logger';
import type { CrmObject, CrmObjectFilters, DealCreateData } from '@/types/crm';

const CREATE_DEAL_TITLE = 'Create deal';
const DEFAULT_DEAL_NAME = 'New deal';

const DEAL_NAME_PROP = 'ahoy_name';
const DEAL_TO_CONTACT_ASSOC = 'ahoy_deal_to_contact';
const DEAL_TO_COMPANY_ASSOC = 'ahoy_deal_to_company';
const CONTACT_TO_COMPANY_ASSOC = 'ahoy_contact_to_company';

/**
 * Maximum contacts associated with a single deal when created from a "select
 * all" bulk selection. Explicit (checkbox) selections are not capped here —
 * the user chose those rows deliberately.
 */
export const MAX_BULK_DEAL_CONTACTS = 50;

/**
 * Page size for the bounded scan that gathers eligible contacts when "select
 * all" is active. Exclusions are filtered client-side; paging in fixed chunks
 * keeps each request/response bounded so the query doesn't scale with
 * arbitrarily large "select all + deselect" lists, while letting us look past
 * clusters of excluded IDs at the top of the sort order.
 */
const BULK_DEAL_FETCH_PAGE_SIZE = 200;

/**
 * Worst-case cap on rows scanned across all pages. If exclusions dominate
 * (e.g. the user deselected hundreds of contacts that all happen to sort to
 * the top), we stop scanning at this many rows rather than walking the entire
 * list. In that pathological case the truncation toast communicates how many
 * eligible contacts were found.
 */
const MAX_BULK_DEAL_FETCH_SCANNED = 1000;

const dealNameWith = (label: string): string => {
  const trimmed = label.trim();
  return trimmed ? `${DEFAULT_DEAL_NAME} with ${trimmed}` : DEFAULT_DEAL_NAME;
};

/** Distinct company entity_ids associated with the given contacts. */
const distinctCompanyIds = (contacts: CrmObject[]): string[] => {
  const ids = new Set<string>();
  for (const contact of contacts) {
    for (const assoc of contact.associations ?? []) {
      if (
        assoc.association_type_name === CONTACT_TO_COMPANY_ASSOC &&
        assoc.target_entity_id
      ) {
        ids.add(assoc.target_entity_id);
      }
    }
  }
  return Array.from(ids);
};

/** Fetch a company's display name; returns null on failure. */
const resolveCompanyName = async (
  orgId: number,
  companyId: string
): Promise<string | null> => {
  try {
    const company = await createCRMObjectClient(orgId, COMPANY).getByEntityId(
      companyId
    );
    return getDisplayName(company);
  } catch (error) {
    logger.error(
      'Failed to resolve company name for deal auto-naming',
      error instanceof Error ? error : undefined,
      { component: 'crm' }
    );
    return null;
  }
};

export interface OpenCreateDealArgs {
  /** Deal name pre-fill (the `ahoy_name` property). */
  name: string;
  /** Contact entity_ids to pre-associate (`ahoy_deal_to_contact`). */
  contactIds?: string[];
  /** Company entity_ids to pre-associate (`ahoy_deal_to_company`). */
  companyIds?: string[];
}

export interface UseCreateDealResult {
  /** Open the modal with explicit pre-fill. Lower-level escape hatch. */
  openCreateDeal: (args: OpenCreateDealArgs) => void;
  /** Single contact → "New deal with {contact}", links contact + its company. */
  createDealForContact: (contact: CrmObject) => void;
  /** Single company → "New deal with {company}", links company. */
  createDealForCompany: (company: CrmObject) => void;
  /**
   * Explicitly selected contacts → one deal with all of them associated.
   * Names from the shared company when they all share one, else "New deal".
   */
  createDealForContacts: (contacts: CrmObject[]) => Promise<void>;
  /**
   * "Select all" bulk selection → one deal, capped at MAX_BULK_DEAL_CONTACTS.
   * Fetches the matching contacts (capped) and applies the same naming rules.
   */
  createDealForContactsByFilters: (
    filters: CrmObjectFilters,
    excludedEntityIds: string[],
    totalCount: number
  ) => Promise<void>;
}

export function useCreateDeal(orgId: number): UseCreateDealResult {
  const { open: openCreateSlideout } = useCreateSlideout();
  const user = useUser();

  const openCreateDeal = useCallback(
    (args: OpenCreateDealArgs): void => {
      if (!user?.id || !orgId) {
        return;
      }
      const dealConfig = getEntityTypeConfig(DEAL).createConfig;
      if (!dealConfig) {
        return;
      }
      const userId = user.id;

      const initialValues: Record<string, unknown> = {
        [DEAL_NAME_PROP]: args.name,
      };
      if (args.contactIds && args.contactIds.length > 0) {
        initialValues[DEAL_TO_CONTACT_ASSOC] = args.contactIds;
      }
      if (args.companyIds && args.companyIds.length > 0) {
        initialValues[DEAL_TO_COMPANY_ASSOC] = args.companyIds;
      }

      openCreateSlideout(
        buildGenericCreateConfig({
          orgId,
          entityType: DEAL,
          title: CREATE_DEAL_TITLE,
          createProperties: dealConfig.createProperties,
          createAssociations: dealConfig.createAssociations,
          initialValues,
          onSubmit: async (data: Record<string, unknown>) => {
            await dealService.create(
              orgId,
              data as unknown as DealCreateData,
              userId
            );
          },
          submitLabel: CREATE_DEAL_TITLE,
        })
      );
    },
    [openCreateSlideout, user, orgId]
  );

  const createDealForContact = useCallback(
    (contact: CrmObject): void => {
      const companyId = distinctCompanyIds([contact])[0];
      openCreateDeal({
        name: dealNameWith(getDisplayName(contact)),
        contactIds: [contact.entity_id],
        companyIds: companyId ? [companyId] : undefined,
      });
    },
    [openCreateDeal]
  );

  const createDealForCompany = useCallback(
    (company: CrmObject): void => {
      openCreateDeal({
        name: dealNameWith(getDisplayName(company)),
        companyIds: [company.entity_id],
      });
    },
    [openCreateDeal]
  );

  const createDealForContacts = useCallback(
    async (contacts: CrmObject[]): Promise<void> => {
      if (contacts.length === 0) {
        return;
      }
      if (contacts.length === 1) {
        createDealForContact(contacts[0]);
        return;
      }

      const contactIds = contacts.map(c => c.entity_id);
      const companyIds = distinctCompanyIds(contacts);

      if (companyIds.length === 1) {
        const companyId = companyIds[0];
        const companyName = await resolveCompanyName(orgId, companyId);
        openCreateDeal({
          name: companyName ? dealNameWith(companyName) : DEFAULT_DEAL_NAME,
          contactIds,
          companyIds: [companyId],
        });
        return;
      }

      openCreateDeal({ name: DEFAULT_DEAL_NAME, contactIds });
    },
    [createDealForContact, openCreateDeal, orgId]
  );

  const createDealForContactsByFilters = useCallback(
    async (
      filters: CrmObjectFilters,
      excludedEntityIds: string[],
      totalCount: number
    ): Promise<void> => {
      if (!orgId) {
        return;
      }
      try {
        const excluded = new Set(excludedEntityIds);
        const client = createCRMObjectClient(orgId, CONTACT);

        // Page through results, filtering exclusions client-side, until we
        // either have MAX_BULK_DEAL_CONTACTS eligible contacts, exhaust the
        // matching set, or hit MAX_BULK_DEAL_FETCH_SCANNED as a hard ceiling.
        // This avoids the pathological case where many excluded IDs cluster
        // at the top of the sort order and a single capped fetch returns
        // fewer eligible contacts than exist.
        const eligible: CrmObject[] = [];
        const associatedObjects: Record<string, CrmObject> = {};
        let offset = 0;
        let scanned = 0;
        while (
          eligible.length < MAX_BULK_DEAL_CONTACTS &&
          scanned < MAX_BULK_DEAL_FETCH_SCANNED
        ) {
          const response = await client.list(
            {
              filter_groups: filters.filter_groups,
              search: filters.search,
              search_properties: filters.search_properties,
              limit: BULK_DEAL_FETCH_PAGE_SIZE,
              offset,
              sort: 'ahoy_updated_at',
              order: 'desc',
            },
            { include_associated_objects: true }
          );
          if (response.associated_objects) {
            Object.assign(associatedObjects, response.associated_objects);
          }
          for (const contact of response.results) {
            if (!excluded.has(contact.entity_id)) {
              eligible.push(contact);
              if (eligible.length >= MAX_BULK_DEAL_CONTACTS) {
                break;
              }
            }
          }
          scanned += response.results.length;
          // Last page reached; nothing more to scan.
          if (response.results.length < BULK_DEAL_FETCH_PAGE_SIZE) {
            break;
          }
          offset += BULK_DEAL_FETCH_PAGE_SIZE;
        }

        if (eligible.length === 0) {
          toast.info('No contacts available to add to a deal.');
          return;
        }
        if (totalCount > eligible.length) {
          toast.info(
            `Adding the first ${eligible.length} of ${totalCount} selected contacts to the deal.`
          );
        }

        const contactIds = eligible.map(c => c.entity_id);
        const companyIds = distinctCompanyIds(eligible);

        if (companyIds.length === 1) {
          const companyId = companyIds[0];
          // The list response hydrates associated_objects, so the company name
          // is usually available without an extra fetch.
          const hydrated = associatedObjects[companyId];
          const companyName = hydrated
            ? getDisplayName(hydrated)
            : await resolveCompanyName(orgId, companyId);
          openCreateDeal({
            name: companyName ? dealNameWith(companyName) : DEFAULT_DEAL_NAME,
            contactIds,
            companyIds: [companyId],
          });
          return;
        }

        openCreateDeal({ name: DEFAULT_DEAL_NAME, contactIds });
      } catch (error) {
        logger.error(
          'Failed to load contacts for bulk deal creation',
          error instanceof Error ? error : undefined,
          { component: 'crm' }
        );
        toast.error("We couldn't create a deal from the selected contacts.");
      }
    },
    [orgId, openCreateDeal]
  );

  return {
    openCreateDeal,
    createDealForContact,
    createDealForCompany,
    createDealForContacts,
    createDealForContactsByFilters,
  };
}
